import { compose } from 'ramda';

import { NO_ELEMENT } from 'yeself-common/DomUtils';
import { ELEMENT_PATHS, ELEMENT_PATH_SEARCH_STRATEGY, NO_PATH } from 'yeself-common/enum/ModelEnum';
import { YES_ELF_HOLDER_TAG } from 'yeself-common/ModelConstant';
import {
    ElementLocation,
    ElementLocationId,
    ElementLocationPath,
    ElementLocationPathSearchStrategy,
    RepresentativeFeature,
} from 'yeself-common/typings/guide/LayerCommon';
import { Iso6391Locale } from 'yeself-common/typings/model/Model';

import { getElementByCssPath as bestDerivedCardinalityMatch } from 'yeself-client-common/searchStrategy/bestDerivedCardinalityMatch';
import { getElementByCssPath as exactRootMatch } from 'yeself-client-common/searchStrategy/exactRootMatch';
import { getElementByCssPath as featureEnrichedBestDerivedCardinalityMatch } from 'yeself-client-common/searchStrategy/featureEnrichedBestDerivedCardinalityMatch';

import { isElementDescendantOfOtherElement, isElementValid } from './ElementUtils';

export const styleSheetLink = (identifier: string, href: string): HTMLLinkElement => {
    const link = document.createElement('link');

    link.setAttribute('rel', 'stylesheet');
    link.setAttribute('type', 'text/css');
    link.setAttribute('id', identifier);
    link.setAttribute('href', href);

    return link;
};

export const styleSheetInlineFromImport = (identifier: string, cssText: string): HTMLStyleElement => {
    const style = document.createElement('style');
    style.setAttribute('id', identifier);
    style.textContent = cssText;

    return style;
};

/**
 * Creates <yes-elf-holder> tag that is container for entire yeself-client-app
 */
export const createYesElfHolder = (): Element => {
    const yesElfHolder = document.createElement(YES_ELF_HOLDER_TAG);
    yesElfHolder.style.setProperty('all', 'unset !important');

    return yesElfHolder;
};

/**
 * Creates ElementLocation object which can be used to retrieve and watch yes-elf-holder element.
 *
 * This can be temporally because services or feature should already have yes-elf-holder reference available.
 */
export const createYesElfHolderElementLocation = (): ElementLocation => ({
    id: `yes-elf-holder` as ElementLocationId,
    paths: [
        {
            id: ELEMENT_PATHS.RELATIVE,
            path: YES_ELF_HOLDER_TAG,
            enabled: true,
            searchStrategy: ELEMENT_PATH_SEARCH_STRATEGY.EXACT_ROOT_MATCH,
        },
    ],
    representativeFeatures: [],
});

/**
 * Function appends existing `yesElfHolder` to body of the document.
 *
 * When appended `yesElfHolder` is removed from DOM it is appended again.
 *
 * @param yesElfHolder yesElfHolder element that is appended
 */
export const appendYesElfHolder = (yesElfHolder: Element): void => {
    document.body.appendChild(yesElfHolder);

    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.removedNodes.forEach((removedElement) => {
                if (isElementDescendantOfOtherElement(removedElement, yesElfHolder)) {
                    document.body.appendChild(yesElfHolder);
                }
            });
        });
    });

    observer.observe(document, {
        childList: true,
        subtree: true,
    });
};

export const getYesElfHolder = (): Element => document.getElementsByTagName(YES_ELF_HOLDER_TAG)[0];

export const filterOutAdoptElements = <T extends Element>(
    elements: ArrayLike<T>,
    doc: Document
): T[] => {
    const yesElfHolder = doc.getElementsByTagName(YES_ELF_HOLDER_TAG)[0];
    if (!yesElfHolder) {
        return Array.from(elements);
    }

    return Array.from(elements).filter((element) => !yesElfHolder.contains(element));
};

export const cloneDocumentWithoutAdoptElements = (document: Document) => {
    const docClone = document.cloneNode(true) as Document;

    const yesElfHolder = docClone.getElementsByTagName(YES_ELF_HOLDER_TAG)[0];
    if (yesElfHolder) {
        yesElfHolder.remove();
    }

    return docClone;
};

const wrapDomNullElement = (elementOrNull) =>
    elementOrNull === undefined || elementOrNull === null || elementOrNull.length === 0
        ? NO_ELEMENT
        : elementOrNull;

// this does not use the searchStrategy. should it? it is only used when recording an element
export const getAllElementsByCssPath = (path) => {
    try {
        return path ? wrapDomNullElement(document.querySelectorAll(path)) : NO_ELEMENT;
    } catch (err) {
        return NO_ELEMENT;
    }
};

const getElementCountIncludingIFrames = (
    countFunction: (doc: Document) => number,
    doc: Document = document
) => {
    let countInCurrentDoc = 0;
    try {
        countInCurrentDoc = countFunction(doc);
    } catch (error) {
        countInCurrentDoc = 0;
    }

    const iframes = Array.from(doc.getElementsByTagName('iframe'));

    const countInFrames = iframes.reduce((acc, iframe) => {
        if (iframe.contentDocument) {
            try {
                return acc + getElementCountIncludingIFrames(countFunction, iframe.contentDocument);
            } catch (error) {
                return acc;
            }
        }
        return acc;
    }, 0);

    return countInCurrentDoc + countInFrames;
};

export const getAllElementsCountByCssPath = (path: string): number => {
    try {
        const elements = document.querySelectorAll(path);
        return filterOutAdoptElements(elements, document).length;
    } catch (error) {
        return 0;
    }
};

const getElementByCssPathDefault = exactRootMatch;

// there was a case where querySelector would throw an error because of selector containing id with spaces
// this can be improved by correctly escaping spaces like "\\ "
export const evaluatePath = (
    [selector, ...restOfPath]: string[],
    parent: Element | ShadowRoot | Document = document
): Element | null => {
    if (!selector) {
        return null;
    }

    const elements = parent.querySelectorAll(selector);

    if (elements.length === 0 || restOfPath.length === 0) {
        return elements[0] || null;
    }

    for (const element of Array.from(elements)) {
        let selectedElement: Element | null = null;
        if (element.shadowRoot && restOfPath.length > 0) {
            selectedElement = evaluatePath(restOfPath, element.shadowRoot);
        }
        if (!selectedElement) {
            selectedElement = evaluatePath(restOfPath, element);
        }
        if (selectedElement) {
            return selectedElement;
        }
    }

    return null;
};

export const evaluateJsPath = ({ path }: { path: string }): Element | null => {
    try {
        return evaluatePath(path.split(','));
    } catch (err) {
        return null;
    }
};

export const minimizeSelectorPath = (
    originalPath: string,
    target: Element,
    separator = ' > '
): string => {
    const parts = originalPath.split(separator).filter(Boolean);

    if (parts.length <= 1) {
        return originalPath;
    }

    for (let i = 0; i < parts.length - 1; i += 1) {
        const testParts = parts.slice(0, i).concat(parts.slice(i + 1));
        const testSelector = testParts.join(separator);
        const matches = document.querySelectorAll(testSelector);

        if (matches.length === 1 && matches[0] === target) {
            return minimizeSelectorPath(testSelector, target, separator);
        }
    }

    return originalPath;
};

const getElementsCountForJsPathFiltered = (
    [selector, ...restOfPath]: string[],
    parent: Element | ShadowRoot | Document = document,
    count = 0
): number => {
    if (!selector) {
        return 0;
    }

    let elements: Element[] = [];

    try {
        const allElements = Array.from(parent.querySelectorAll(selector));

        const yesElfHolder =
            parent instanceof Document
                ? parent.getElementsByTagName(YES_ELF_HOLDER_TAG)[0]
                : document.getElementsByTagName(YES_ELF_HOLDER_TAG)[0];

        elements = yesElfHolder ? allElements.filter((el) => !yesElfHolder.contains(el)) : allElements;
    } catch (error) {
        return 0;
    }

    if (restOfPath.length === 0) {
        return elements.length;
    }

    let currentCount = count;

    for (const element of elements) {
        try {
            const canQueryNext = (() => {
                try {
                    return !element.shadowRoot || element.querySelector(restOfPath[0]);
                } catch (error) {
                    return false;
                }
            })();

            const nextParent = element.shadowRoot && !canQueryNext ? element.shadowRoot : element;

            currentCount += getElementsCountForJsPathFiltered(restOfPath, nextParent, 0);
        } catch (error) {
            /* skip updating count */
        }
    }

    return currentCount;
};

export const getAllElementsCountForJsPath = (path: string): number => {
    try {
        return getElementsCountForJsPathFiltered(path.split(','), document);
    } catch (error) {
        return 0;
    }
};

const getElementByCssPathForStrategy = {
    [ELEMENT_PATH_SEARCH_STRATEGY.EXACT_ROOT_MATCH]: exactRootMatch,
    [ELEMENT_PATH_SEARCH_STRATEGY.BEST_DERIVED_CARDINALITY_MATCH]: bestDerivedCardinalityMatch,
    [ELEMENT_PATH_SEARCH_STRATEGY.FEATURES_ENRICHED_BEST_DERIVED_CARDINALITY_MATCH]:
        featureEnrichedBestDerivedCardinalityMatch,
    [ELEMENT_PATH_SEARCH_STRATEGY.JS_PATH_QUERY]: evaluateJsPath,
};

const createGetElementByCssPath =
    (searchStrategy: ElementLocationPathSearchStrategy) =>
    (pathObject: ElementLocationPath, representativeFeatures: RepresentativeFeature[]): Element => {
        try {
            const fn = compose(
                wrapDomNullElement,
                getElementByCssPathForStrategy[searchStrategy] || getElementByCssPathDefault
            ) as any;
            return pathObject.path ? fn({ ...pathObject, representativeFeatures }) : NO_ELEMENT;
        } catch (err) {
            // todo: apm?
            return NO_ELEMENT;
        }
    };

export const getElementByXpathImmediate = (path) => {
    try {
        const element = document.evaluate(
            path,
            document,
            null,
            XPathResult.FIRST_ORDERED_NODE_TYPE,
            null
        ).singleNodeValue;
        return wrapDomNullElement(element);
    } catch (err) {
        return NO_ELEMENT;
    }
};

export const getElementByXpathDeferred = (path): Promise<Element | typeof NO_ELEMENT> =>
    new Promise((resolve) => {
        requestIdleCallback(() => {
            try {
                const element = document.evaluate(
                    path,
                    document,
                    null,
                    XPathResult.FIRST_ORDERED_NODE_TYPE,
                    null,
                ).singleNodeValue;
                resolve(wrapDomNullElement(element));
            } catch (err) {
                resolve(NO_ELEMENT);
            }
        });
    });

export const getElementsByXPaths = (paths: string[]): Promise<(Element | null | undefined)[]> =>
    Promise.all(paths.map((path) => getElementByXpathDeferred(path)));

const getFilteredXPathElements = (path: string, doc: Document): number => {
    try {
        const result = doc.evaluate(path, doc, null, XPathResult.ORDERED_NODE_SNAPSHOT_TYPE, null);

        const elements = Array.from(
            { length: result.snapshotLength },
            (_, i) => result.snapshotItem(i) as Element
        );

        return filterOutAdoptElements(elements, doc).length;
    } catch (error) {
        return 0;
    }
};

export const getAllElementsCountByXpath = (path: string): number =>
    getFilteredXPathElements(path, document);

export const elementByPath = (
    pathObject: ElementLocationPath,
    representativeFeatures: RepresentativeFeature[]
) => {
    switch (pathObject.id) {
        case ELEMENT_PATHS.XPATH:
        case ELEMENT_PATHS.XPATH_LIMITED:
            return getElementByXpathImmediate(pathObject.path);
        default:
            return createGetElementByCssPath(pathObject.searchStrategy)(
                pathObject,
                representativeFeatures
            );
    }
};

export const calculateMatches = (
    path: ElementLocationPath,
    locale: Iso6391Locale
): number => {
    switch (path.id) {
        case ELEMENT_PATHS.XPATH:
            return locale && path.path && path.path[locale] && path.path[locale] !== NO_PATH
                ? getAllElementsCountByXpath(path.path[locale])
                : 0;
        case ELEMENT_PATHS.XPATH_LIMITED:
            return path.path && path.path !== NO_PATH ? getAllElementsCountByXpath(path.path) : 0;
        case ELEMENT_PATHS.JS_PATH:
        case ELEMENT_PATHS.JS_PATH_NON_RELATIVE:
            return path.path && path.path !== NO_PATH ? getAllElementsCountForJsPath(path.path) : 0;
        default:
            return path.path && path.path !== NO_PATH ? getAllElementsCountByCssPath(path.path) : 0;
    }
};

export const elementByPathAsync = async (
    pathObject: ElementLocationPath,
    representativeFeatures: RepresentativeFeature[]
): Promise<Element | null> => {
    switch (pathObject.id) {
        case ELEMENT_PATHS.XPATH:
        case ELEMENT_PATHS.XPATH_LIMITED:
            return getElementByXpathDeferred(pathObject.path);
        default:
            return createGetElementByCssPath(pathObject.searchStrategy)(
                pathObject,
                representativeFeatures
            );
    }
};

/**
 * @deprecated use resolveElementPathByElementLocatiobObject without aged NO_ELEMENT wrapper.
 *
 * Returns the object possibly containing first element and pathId that matches given `location` object
 */
export const resolvePath = ({ paths, representativeFeatures }) => {
    for (const pathObject of paths) {
        if (pathObject.enabled) {
            const element = elementByPath(pathObject, representativeFeatures);
            if (isElementValid(element)) return { pathId: pathObject.id, element };
        }
    }
    return {};
};

/* eslint-disable no-await-in-loop */
export const resolvePathAsync = async ({ paths, representativeFeatures }) => {
    const xpathPaths = paths.filter(pathObject => pathObject.enabled && pathObject.id === ELEMENT_PATHS.XPATH);
    const nonXpathPaths = paths.filter(pathObject => pathObject.enabled && pathObject.id !== ELEMENT_PATHS.XPATH);

    const xpathResults = await getElementsByXPaths(xpathPaths.map(pathObject => pathObject.path));

    for (let i = 0; i < xpathResults.length; i += 1) {
        const element = xpathResults[i];

        if (isElementValid(element)) {
            return { pathId: xpathPaths[i].id, element };
        }
    }

    for (const pathObject of nonXpathPaths) {
        const element = await elementByPathAsync(pathObject, representativeFeatures);
        if (isElementValid(element)) return { pathId: pathObject.id, element };
    }

    return {};
};
/* eslint-enable no-await-in-loop */

/**
 * @deprecated use GetElementByLocationObject without aged NO_ELEMENT wrapper.
 *
 * Returns element that belong to specified location object.
 */
export const resolveLocator = (location: ElementLocation): Element => {
    const pathObject = resolvePath(location);
    return wrapDomNullElement(pathObject.element);
};

export const resolveLocatorAsync = async (location: ElementLocation): Promise<Element> => {
    const pathObject = await resolvePathAsync(location);
    return wrapDomNullElement(pathObject.element);
};

/**
 * Returns the first element and pathId that matches given `location` object.
 *
 * When no match is found null is returned.
 *
 * @param location Location object by which the element is retrieved.
 */
export const resolveElementPathByElementLocationObject = ({
  paths,
  representativeFeatures,
}: {
    paths: ElementLocationPath[];
    representativeFeatures: RepresentativeFeature[];
}): { element: HTMLElement | null; pathId: string | null } => {
    for (const pathObject of paths) {
        if (pathObject.enabled) {
            const element = elementByPath(pathObject, representativeFeatures);
            if (isElementValid(element)) return { pathId: pathObject.id, element };
        }
    }
    return {
        pathId: null,
        element: null,
    };
};

export const resolveElementPathByElementLocationObjectAsync = async ({
    paths,
    representativeFeatures,
}) => {
    const enabledPaths = paths.filter((pathObject) => pathObject.enabled);

    const results = await Promise.all(
        enabledPaths.map(async (pathObject) => {
            const element = await elementByPathAsync(pathObject, representativeFeatures);
            return isElementValid(element)
                ? { pathId: pathObject.id, element }
                : null;
        })
    );

    const validResult = results.find((result) => result !== null);

    return validResult || { pathId: null, element: null };
};

/**
 * Return true when element is or has a parent yes-elf-holder
 */
export const isWithinYesElfTag = (element: Element | HTMLElement | Node): boolean => {
    const { body } = document;
    const yesElfHolder = getYesElfHolder();
    return body === element ? false : !!yesElfHolder && yesElfHolder.contains(element);
};
